import { memo, useState, useCallback } from 'react'
import { PencilIcon, TrashIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * MessageActions - Edit/Delete buttons for own messages
 * 
 * Desktop: actions are hidden and revealed on parent group-hover (pointer devices only).
 * Mobile/touch: a ⋮ button is always visible; tapping it toggles the edit/delete buttons inline.
 * 
 * @atomic - Shows only for message owner
 */
const MessageActions = memo(function MessageActions({ 
  message, 
  currentUserId,
  isAdmin,
  onEdit, 
  onDelete,
  isEditing 
}) {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleToggle = useCallback((e) => {
    e.stopPropagation()
    setMobileOpen(prev => !prev)
  }, [])

  const handleEdit = useCallback((e) => {
    e.stopPropagation()
    setMobileOpen(false)
    onEdit(message)
  }, [onEdit, message])

  const handleDelete = useCallback((e) => {
    e.stopPropagation()
    setMobileOpen(false)
    onDelete(message.id)
  }, [onDelete, message.id])

  const isOwnMessage = message.userId === currentUserId
  // Show actions for own messages, or for admins (delete only) on any non-deleted message
  if ((!isOwnMessage && !isAdmin) || message.isDeleted) {
    return null
  }

  if (isEditing) return null

  const actionButtons = (
    <>
      {isOwnMessage && (
        <button
          onClick={handleEdit}
          onTouchEnd={handleEdit}
          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title={t('channels.message.edit')}
          aria-label={t('channels.message.edit')}
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={handleDelete}
        onTouchEnd={handleDelete}
        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title={t('channels.message.delete')}
        aria-label={t('channels.message.delete')}
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </>
  )

  return (
    <div className="flex items-center gap-1">
      {/* Desktop: reveal on parent group-hover (pointer/hover-capable devices only) */}
      <div className="can-hover:flex hidden items-center gap-1 can-hover:opacity-0 can-hover:group-hover:opacity-100 transition-opacity">
        {actionButtons}
      </div>

      {/* Mobile/touch: ⋮ button always visible; tap to expand actions inline */}
      <div className="can-hover:hidden flex items-center gap-1">
        {mobileOpen && actionButtons}
        <button
          onClick={handleToggle}
          onTouchEnd={handleToggle}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          title={t('common.actions', 'Actions')}
          aria-label={t('common.actions', 'Actions')}
          aria-expanded={mobileOpen}
        >
          <EllipsisVerticalIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
})

export default MessageActions
